import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Stack
} from "@mui/material";
import { 
  AccessTime, 
  DateRange, 
  AccountBalanceWallet, 
  ToggleOn, 
  Save, 
  SettingsSystemDaydream 
} from "@mui/icons-material";
import api from "../../api/axios";
import toast from "react-hot-toast";

const CompanyPolicyPage = () => {

  /* ================= SAFE DEFAULT STATE ================= */
  const [policy, setPolicy] = useState({
    officeStartTime: "",
    lateThreshold: "",
    halfDayThreshold: "",
    lateCountLimit: 6,
    payrollCycleType: "21_20",
    payrollStartDay: 21,
    payrollEndDay: 20,
    enableLatePenalty: true,
    enableHalfDay: true,
    enableWeekOff: true,
    pfEnabled: false,
    pfPercentage: 12,
    esiEnabled: false,
    esiPercentage: 1.75
  });

  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, []);

  /* ================= TIME FORMAT FIX ================= */
  const formatTime = (time) => {
    if (!time) return "";
    return time.substring(0, 5); // "09:30:00" → "09:30"
  };

  /* ================= FETCH POLICY ================= */
  const fetchPolicy = async () => {
    try {
      const res = await api.get("/company-policy");

      if (!res.data) {
        setInitializing(true);

        await api.post("/company-policy/init");

        const retry = await api.get("/company-policy");

        setPolicy(prev => ({
          ...prev,
          ...retry.data
        }));

        setInitializing(false);
      } else {
        setPolicy(prev => ({
          ...prev,
          ...res.data
        }));
      }

    } catch (err) {
      console.error(err);
      toast.error("Failed to sync system policies");
    } finally {
      setLoading(false);
    }
  };

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setPolicy(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      await api.post("/company-policy", policy);
      toast.success("Enterprise configuration saved successfully");
    } catch (e) {
      toast.error("Failed to commit configuration update");
    }
  };

  /* ================= LOADING UI ================= */
  if (loading || initializing) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: '#1e3a8a' }} />
        <Typography mt={3} variant="h6" color="text.secondary" fontWeight={600}>
          {initializing ? "Configuring default system templates..." : "Syncing global business settings..."}
        </Typography>
      </Box>
    );
  }

  /* ================= UI ================= */
  return (
    <Box sx={{ p: 4, backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* HEADER HERO */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4, 
          background: "linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)", 
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.5px" }}>
            Enterprise Rule Book
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "#93c5fd", mt: 0.5, fontWeight: 500 }}>
            Modify configurations across attendance shifts, compliance deductions, and cycle boundaries.
          </Typography>
        </Box>
        <Avatar sx={{ width: 56, height: 56, bgcolor: "rgba(255,255,255,0.1)" }}>
          <SettingsSystemDaydream sx={{ fontSize: 32, color: "#93c5fd" }} />
        </Avatar>
      </Paper>

      <Grid container spacing={4}>
        {/* LEFT COLUMN: CRITICAL CONFIGS */}
        <Grid item xs={12} md={8}>
          <Stack spacing={4}>
            
            {/* ATTENDANCE SECTION */}
            <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#eff6ff", color: "#1e4ed8", width: 40, height: 40 }}><AccessTime /></Avatar>
                  <Typography variant="h6" fontWeight={800} color="#0f172a">Attendance & Shift Gateways</Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Office Shift Starts"
                      type="time"
                      name="officeStartTime"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={formatTime(policy?.officeStartTime)}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Late Triggers After"
                      type="time"
                      name="lateThreshold"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={formatTime(policy?.lateThreshold)}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Half Day Penalty"
                      type="time"
                      name="halfDayThreshold"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={formatTime(policy?.halfDayThreshold)}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Late Grace Allowance"
                      name="lateCountLimit"
                      type="number"
                      fullWidth
                      helperText="Monthly warning cap"
                      value={policy?.lateCountLimit || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* PAYROLL SECTION */}
            <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#ecfdf5", color: "#059669", width: 40, height: 40 }}><DateRange /></Avatar>
                  <Typography variant="h6" fontWeight={800} color="#0f172a">Payroll Cycle Configuration</Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Cycle Identity Code"
                      name="payrollCycleType"
                      fullWidth
                      value={policy?.payrollCycleType || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} >
                    <TextField
                      label="Cycle Start Day"
                      name="payrollStartDay"
                      type="number"
                      fullWidth
                      value={policy?.payrollStartDay || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Cycle Cutoff Day"
                      name="payrollEndDay"
                      type="number"
                      fullWidth
                      value={policy?.payrollEndDay || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN: SWITCHES & STATUTORIES */}
        <Grid item xs={12} md={4}>
          <Stack spacing={4}>
            
            {/* COMPLIANCE STATUTORY */}
            <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#fdf2f8", color: "#db2777", width: 40, height: 40 }}><AccountBalanceWallet /></Avatar>
                  <Typography variant="h6" fontWeight={800} color="#0f172a">Statutory Deductions</Typography>
                </Box>

                <Box sx={{ backgroundColor: "#f8fafc", p: 2, borderRadius: 3, mb: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={policy?.pfEnabled || false} name="pfEnabled" onChange={handleChange} color="primary" />}
                    label={<Typography fontWeight={700} variant="body2">Provident Fund (PF)</Typography>}
                  />
                  <TextField
                    label="PF Base %"
                    name="pfPercentage"
                    type="number"
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5, backgroundColor: "#fff" }}
                    disabled={!policy?.pfEnabled}
                    value={policy?.pfPercentage || ""}
                    onChange={handleChange}
                  />
                </Box>

                <Box sx={{ backgroundColor: "#f8fafc", p: 2, borderRadius: 3 }}>
                  <FormControlLabel
                    control={<Switch checked={policy?.esiEnabled || false} name="esiEnabled" onChange={handleChange} color="primary" />}
                    label={<Typography fontWeight={700} variant="body2">Employee State Insurance (ESI)</Typography>}
                  />
                  <TextField
                    label="ESI Base %"
                    name="esiPercentage"
                    type="number"
                    fullWidth
                    size="small"
                    sx={{ mt: 1.5, backgroundColor: "#fff" }}
                    disabled={!policy?.esiEnabled}
                    value={policy?.esiPercentage || ""}
                    onChange={handleChange}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* TOGGLES */}
            <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.02)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: "#fff7ed", color: "#ea580c", width: 40, height: 40 }}><ToggleOn /></Avatar>
                  <Typography variant="h6" fontWeight={800} color="#0f172a">Feature Rules Logic</Typography>
                </Box>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch checked={policy?.enableLatePenalty || false} name="enableLatePenalty" onChange={handleChange} />}
                    label={<Typography variant="body2" fontWeight={600}>Enforce Late Salary Deductions</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={policy?.enableHalfDay || false} name="enableHalfDay" onChange={handleChange} />}
                    label={<Typography variant="body2" fontWeight={600}>Enable Short Duration Half-Days</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={policy?.enableWeekOff || false} name="enableWeekOff" onChange={handleChange} />}
                    label={<Typography variant="body2" fontWeight={600}>Automate Paid Calendared Week-Offs</Typography>}
                  />
                </Stack>
              </CardContent>
            </Card>

          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{
            px: 6,
            py: 2,
            borderRadius: 3,
            fontWeight: 800,
            fontSize: "16px",
            background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)",
            boxShadow: "0 4px 14px 0 rgba(30,58,138,0.3)"
          }}
        >
          Commit Configuration Updates
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyPolicyPage;